import { create } from 'zustand';

/**
 * Where the search field currently lives on `/app/search`.
 *
 * The page shows a large field in its header while you're at the top; scroll
 * down and it hands off to the compact one in the top nav. Only ONE of the two
 * is mounted at a time — two live search inputs on screen is confusing, and
 * whichever you weren't typing in would go stale.
 *
 * A store rather than props because the two ends are far apart in the tree:
 * `SearchPage` owns the scroll signal, `GlobalSearchBar` lives in `TopNavigation`
 * above the router outlet. UI-only and memory-only — never persisted, since
 * "am I scrolled down" must not survive a reload.
 */
interface SearchHeaderState {
  /** True once the page has scrolled far enough to hand the field to the nav. */
  isDocked: boolean;
  setDocked: (isDocked: boolean) => void;
  /**
   * Bumped when the nav field hands the query back to the page field, so the
   * page can take focus without either side reaching into the other's DOM.
   *
   * A counter, not a boolean: two handoffs in a row must both fire, and a
   * boolean would need resetting afterwards — which is a second write that can
   * race the read.
   */
  pageFieldFocusRequest: number;
  requestPageFieldFocus: () => void;
}

export const useSearchHeaderStore = create<SearchHeaderState>((set) => ({
  // Defaults to docked so every OTHER page keeps its nav field. `SearchPage`
  // undocks on mount and re-docks on unmount.
  isDocked: true,
  setDocked: (isDocked) => set((prev) => (prev.isDocked === isDocked ? prev : { isDocked })),
  pageFieldFocusRequest: 0,
  requestPageFieldFocus: () =>
    set((prev) => ({ pageFieldFocusRequest: prev.pageFieldFocusRequest + 1 })),
}));

/**
 * Scroll depth at which the handoff happens, in px.
 *
 * Deliberately larger than a trackpad's smallest flick: the swap is a visible
 * change, and a threshold of a few px would strobe the two fields while someone
 * rests two fingers on the pad. `SCROLL_DOCK_RELEASE` is lower than
 * `SCROLL_DOCK_ENGAGE` on purpose — the gap between them is hysteresis, so
 * scrolling back and forth across the boundary can't flicker.
 */
export const SCROLL_DOCK_ENGAGE = 48;
export const SCROLL_DOCK_RELEASE = 16;
